import React, { useState, useEffect } from "react";
import { ACCOUNT_OWNER, EXTERNAL_INVOICE_RECEIVER } from "/shared/constants";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import LoadingPanel from "@/components/loadingPanel";
import useSubscription from "@/hooks/subscriptions/useSubscription";
import useCompanyUsers from "@/hooks/companies/useCompanyUsers";
import useUpdateSubscriptionInvoiceReiceiver from "@/hooks/subscriptions/useUpdateSubscriptionInvoiceReceiver";
import useCompanyFromUrl from "@/hooks/companies/useCompanyFromUrl";
import { Trans, useTranslation } from "react-i18next";
import ExternalReferenceModal from "@/components/subscriptions/externalReferenceModal";
import { CircleHelp, Pen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { withRouter } from "@/interfaces/router";

const BillingInvoiceReceiverDetails = () => {
  const { t } = useTranslation();
  const [selectedReceiver, setSelectedReceiver] = useState(ACCOUNT_OWNER);
  const [externalReferenceModalOpen, setExternalReferenceModalOpen] =
    useState(false);

  const { data: companyQuery, isLoading: isLoadingCompany } =
    useCompanyFromUrl();
  const company = companyQuery?.data;
  const companyId = company?.id;

  const {
    mutate: updateSubscriptionInvoiceReiceiver,
    isPending: isUpdatingSubscriptionInvoiceReceiver,
  } = useUpdateSubscriptionInvoiceReiceiver(companyId);

  const { data: companyUsersQuery, isLoading: isLoadingCompanyUsers } =
    useCompanyUsers();
  const companyUsers = companyUsersQuery?.data;

  const { data: subscriptionQuery, isLoading: isLoadingSubscription } =
    useSubscription();
  const subscription = subscriptionQuery?.data;
  const invoiceReceiver = subscription?.invoiceReciever; // Note: correct for typo in backend `reciever` -> `receiver`

  useEffect(() => {
    if (!invoiceReceiver) return;
    setSelectedReceiver(invoiceReceiver);
  }, [invoiceReceiver]);

  if (isLoadingSubscription || isLoadingCompanyUsers || isLoadingCompany)
    return <LoadingPanel />;

  const companyUsersOptions = companyUsers
    ?.filter((u) => u.name)
    ?.map((user) => {
      const email = user.emails.find((email) => user.defaultEmail === email.id);

      return {
        value: user.id,
        label: (
          <div className="inline-flex gap-4">
            <span>{user.name}</span>
            <span className="text-muted-foreground">({email?.email})</span>
          </div>
        ),
      };
    });

  const options = [
    {
      label: t(
        "subscriptions.notification_panel.options.external_invoice_receiver",
      ),
      value: EXTERNAL_INVOICE_RECEIVER,
    },
    {
      label: t("subscriptions.notification_panel.options.company_owner"),
      value: ACCOUNT_OWNER,
    },
    ...companyUsersOptions,
  ];

  const currentReceiverUser = companyUsers?.find((user) =>
    selectedReceiver === ACCOUNT_OWNER
      ? user.id === company?.superUser
      : user.id === invoiceReceiver,
  );

  let currentReceiverEmail = currentReceiverUser?.emails.find(
    (email) => email.id === currentReceiverUser.defaultEmail,
  )?.email;

  let currentReceiverName = currentReceiverUser?.name;

  if (selectedReceiver === EXTERNAL_INVOICE_RECEIVER) {
    currentReceiverName = subscription.externalInvoiceName;
    currentReceiverEmail = subscription.externalInvoiceEmail;
  }

  return (
    <div>
      <div className="flex flex-col gap-6 p-2">
        <div>
          <label>
            {t(
              "subscriptions.notification_panel.label.member_to_recieve_emails",
            )}
          </label>
          <Select
            value={selectedReceiver}
            onValueChange={(value) => {
              if (value === EXTERNAL_INVOICE_RECEIVER) {
                setSelectedReceiver(value);
                return setExternalReferenceModalOpen(true);
              }

              updateSubscriptionInvoiceReiceiver({
                invoiceReceiver: value,
              });
            }}
          >
            <SelectTrigger isLoading={isUpdatingSubscriptionInvoiceReceiver}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {options.map(({ label, value }) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-start justify-between gap-4">
          <div className="[&>p]:mb-3">
            <Trans
              i18nKey="subscriptions.notification_panel.info_text"
              values={{
                name: currentReceiverName,
                email: currentReceiverEmail,
              }}
            />
          </div>
          {selectedReceiver === EXTERNAL_INVOICE_RECEIVER && (
            <Button
              className="rounded-full"
              variant="outline"
              onClick={() => setExternalReferenceModalOpen(true)}
              title={t("subscriptions.notifications.edit")}
            >
              <Pen size={16} />
            </Button>
          )}
        </div>
        <div className="grid grid-cols-[20px_1fr] font-bold text-muted-foreground">
          <CircleHelp size={16} className="mt-[1px]" />
          <div>
            <Trans
              i18nKey="subscriptions.billing_invoice_receivers.contact_support"
              components={[
                <a
                  key="support-link"
                  className="font-semibold text-primary"
                  href="javascript:Beacon?.('open');"
                />,
              ]}
            />
          </div>
        </div>
      </div>
      {externalReferenceModalOpen && (
        <ExternalReferenceModal
          onCancel={() => setExternalReferenceModalOpen(false)}
        />
      )}
    </div>
  );
};

export default withRouter(BillingInvoiceReceiverDetails);
